import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Heading } from '../../ui/heading';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  CloudArrowUpIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { 
  getOmbudsmanDecisions,
  getOmbudsmanStatistics,
  getDecisionRelatedSignals,
  getOmbudsmanDecisionById,
  deleteOmbudsmanDecisions,
  backfillOmbudsmanVectors
} from '../../../utils/api';
import { 
  OmbudsmanDecision, 
  OmbudsmanStatistics,
  OmbudsmanFilters,
  OmbudsmanDecisionListResponse 
} from '../../../types/ombudsman';
import OmbudsmanImportModal from '../../ombudsman/OmbudsmanImportModal';
import OmbudsmanStatsDashboard from '../../ombudsman/OmbudsmanStatsDashboard';
import ConfirmationModal from '../../ConfirmationModal';
import { Timestamp } from '../../ui/timestamp';

const OmbudsmanDecisionsView: React.FC = () => {
  const [decisions, setDecisions] = useState<OmbudsmanDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedDecision, setSelectedDecision] = useState<OmbudsmanDecision | null>(null);
  const [statistics, setStatistics] = useState<OmbudsmanStatistics | null>(null);
  const [relatedSignals, setRelatedSignals] = useState<any[]>([]);
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedDecisions, setSelectedDecisions] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pollVectors, setPollVectors] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState<OmbudsmanFilters>({
    decision_status: '',
    industry_sector: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const loadDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      
      const response: OmbudsmanDecisionListResponse = await getOmbudsmanDecisions(
        skip, 
        pageSize, 
        filterParams
      );
      
      setDecisions(response.decisions);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Error loading ombudsman decisions:', error);
      toast.error('Failed to load ombudsman decisions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await getOmbudsmanStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  }, []);

  const loadRelatedSignals = useCallback(async (decisionId: string) => {
    setLoadingSignals(true);
    try {
      const response = await getDecisionRelatedSignals(decisionId);
      setRelatedSignals(response.related_signals || []);
    } catch (error) {
      console.error('Error loading related signals:', error);
      toast.error('Failed to load related signals');
      setRelatedSignals([]);
    } finally {
      setLoadingSignals(false);
    }
  }, []);

  useEffect(() => {
    loadDecisions();
  }, [loadDecisions]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Poll vector readiness after triggering backfill
  useEffect(() => {
    if (!pollVectors) return;
    const iv = setInterval(async () => {
      const stats = await getOmbudsmanStatistics();
      setStatistics(stats);
      const ready = stats?.vector_stats?.full_text_vectors || 0;
      if (stats && ready >= (stats.total_decisions || 0)) {
        clearInterval(iv);
        setPollVectors(false);
        toast.success('Vector backfill completed');
      }
    }, 2000);
    const timeout = setTimeout(() => { clearInterval(iv); setPollVectors(false); }, 120000);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, [pollVectors]);

  const handleFilterChange = (field: keyof OmbudsmanFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleDecisionSelect = async (decision: OmbudsmanDecision) => {
    try {
      const detail = await getOmbudsmanDecisionById(decision.id);
      // Debug: verify full_text presence in detail
      try {
        // eslint-disable-next-line no-console
        console.debug('[Ombudsman] Detail fetched', {
          id: decision.id,
          ref: decision.reference,
          hasFullText: !!detail?.full_text,
          fullTextLen: detail?.full_text ? String(detail.full_text).length : 0,
        });
      } catch {}
      setSelectedDecision({ ...decision, ...detail });
    } catch {
      setSelectedDecision(decision);
    }
    setRelatedSignals([]);
    loadRelatedSignals(decision.id);
  };

  const handleImportSuccess = () => {
    loadDecisions();
    loadStatistics();
    setShowImportModal(false);
  };

  const handleSelectDecision = (decisionId: string) => {
    setSelectedDecisions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(decisionId)) {
        newSet.delete(decisionId);
      } else {
        newSet.add(decisionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDecisions.size === decisions.length) {
      setSelectedDecisions(new Set());
    } else {
      setSelectedDecisions(new Set(decisions.map(d => d.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDecisions.size === 0) return;
    
    try {
      await deleteOmbudsmanDecisions(Array.from(selectedDecisions));
      toast.success(`Deleted ${selectedDecisions.size} decisions`);
      setSelectedDecisions(new Set());
      setShowDeleteModal(false);
      loadDecisions();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting decisions:', error);
      toast.error('Failed to delete decisions');
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteOmbudsmanDecisions();
      toast.success('All decisions deleted');
      setSelectedDecisions(new Set());
      setShowDeleteModal(false);
      loadDecisions();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting all decisions:', error);
      toast.error('Failed to delete all decisions');
    }
  };

  const getStatusIcon = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'upheld':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'not upheld':
      case 'not_upheld':
      case 'notupheld':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'partially upheld':
      case 'partly upheld':
      case 'partly_upheld':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'upheld':
        return 'bg-red-100 text-red-800';
      case 'not upheld':
      case 'not_upheld':
      case 'notupheld':
        return 'bg-green-100 text-green-800';
      case 'partially upheld':
      case 'partly upheld':
      case 'partly_upheld':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (selectedDecision) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedDecision(null)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            ← Back to Decisions
          </button>
        </div>

        {/* Decision Detail */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <Heading level={2} className="text-xl text-gray-900">
                  {selectedDecision.reference}
                </Heading>
                <p className="text-gray-600">
                  {selectedDecision.business_name || 'Unknown Business'} • <Timestamp value={selectedDecision.decision_date} format="date-only" />
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedDecision.decision_status)}`}>
                  {getStatusIcon(selectedDecision.decision_status)}
                  {selectedDecision.decision_status}
                </span>
                {selectedDecision.pdf_url && (
                  <a
                    href={selectedDecision.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800 text-sm border border-blue-200 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    View PDF
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Summary */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Summary</h3>
              <p className="text-gray-700 leading-relaxed">{selectedDecision.summary}</p>
            </div>

            {/* Full Text */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Full Text</h3>
              {selectedDecision.full_text && selectedDecision.full_text.trim().length > 0 ? (
                <div className="prose max-w-none whitespace-pre-wrap text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {selectedDecision.full_text}
                </div>
              ) : (
                <div className="text-sm text-gray-600 italic">
                  Full text not available for this decision.
                </div>
              )}
            </div>

            {/* Decision Sections */}
            {selectedDecision.complaint_section && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">The Complaint</h3>
                <p className="text-gray-700 leading-relaxed">{selectedDecision.complaint_section}</p>
              </div>
            )}

            {selectedDecision.what_happened_section && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">What Happened</h3>
                <p className="text-gray-700 leading-relaxed">{selectedDecision.what_happened_section}</p>
              </div>
            )}

            {selectedDecision.reasoning_section && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Reasoning</h3>
                <p className="text-gray-700 leading-relaxed">{selectedDecision.reasoning_section}</p>
              </div>
            )}

            {selectedDecision.final_decision_section && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Final Decision</h3>
                <p className="text-gray-700 leading-relaxed">{selectedDecision.final_decision_section}</p>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <span className="text-sm font-medium text-gray-500">Industry Sector</span>
                <p className="text-gray-900">{selectedDecision.industry_sector}</p>
              </div>
              {selectedDecision.page_count && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Pages</span>
                  <p className="text-gray-900">{selectedDecision.page_count}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related Signals */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Related Signals</h3>
            <p className="text-sm text-gray-600">
              Signals that show similar patterns to this ombudsman decision
            </p>
          </div>
          
          <div className="p-6">
            {loadingSignals ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Finding related signals...</p>
              </div>
            ) : relatedSignals.length === 0 ? (
              <div className="text-center py-8">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h4 className="mt-2 text-sm font-medium text-gray-900">No related signals found</h4>
                <p className="mt-1 text-sm text-gray-500">
                  No signals match this ombudsman decision with sufficient similarity.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Found {relatedSignals.length} signals with similar patterns (≥70% similarity)
                </div>
                
                {relatedSignals.map((signal, index) => (
                  <div key={signal.signal_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {signal.signal_reference || `Signal ${signal.signal_id.slice(0, 8)}`}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            signal.confidence_level === 'high' ? 'bg-green-100 text-green-800' :
                            signal.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {signal.similarity_score ? `${(signal.similarity_score * 100).toFixed(1)}% match` : 'Low match'}
                          </span>
                          {signal.severity && (
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                              signal.severity === 'High' ? 'bg-red-100 text-red-800' :
                              signal.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {signal.severity} Severity
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-2">
                          {signal.customer_name && (
                            <span className="font-medium">Customer: {signal.customer_name}</span>
                          )}
                          {signal.created_at && (
                            <span className="ml-4">
                              Date: <Timestamp value={signal.created_at} format="date-only" />
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-3">
                          {signal.verbatim}
                        </p>
                        
                        {signal.categories && signal.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {signal.categories.map((category: string, idx: number) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                {category}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500 italic">
                          {signal.match_reasoning}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {relatedSignals.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Analysis:</strong> These signals show similar patterns to the ombudsman decision "{selectedDecision.reference}". 
                      This suggests potential precedent for how similar customer issues might be viewed by the ombudsman.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2} weight="bold" className="text-gray-900">Ombudsman Decisions</Heading>
          <p className="text-gray-600">
            Browse and analyze ombudsman decisions for intelligent signal matching
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
          </button>
          <button
            onClick={async () => {
              try {
                const res = await backfillOmbudsmanVectors();
                toast.info('Backfill started. This will populate vector embeddings for similarity search.');
                setPollVectors(true);
              } catch (e) {
                console.error('Backfill error', e);
                toast.error('Failed to start backfill');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Populate vector embeddings for similarity search"
          >
            <ClockIcon className="h-5 w-5" />
            Backfill Vectors
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            Import Decisions
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      {statistics && (
        <OmbudsmanStatsDashboard statistics={statistics} />
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search decisions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Decision Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Decision Status
              </label>
              <select
                value={filters.decision_status}
                onChange={(e) => handleFilterChange('decision_status', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Upheld">Upheld</option>
                <option value="Not Upheld">Not Upheld</option>
                <option value="Partially Upheld">Partially Upheld</option>
              </select>
            </div>

            {/* Industry Sector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry Sector
              </label>
              <select
                value={filters.industry_sector}
                onChange={(e) => handleFilterChange('industry_sector', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Sectors</option>
                <option value="Insurance">Insurance</option>
                <option value="Banking">Banking</option>
                <option value="Investment">Investment</option>
                <option value="Credit">Credit</option>
                <option value="Mortgage">Mortgage</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      {selectedDecisions.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
          <span className="text-blue-800">
            {selectedDecisions.size} decision(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Decisions List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading decisions...</p>
          </div>
        ) : decisions.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No decisions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or import some decisions.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedDecisions.size === decisions.length && decisions.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Decision
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Industry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {decisions.map((decision) => (
                    <tr key={decision.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedDecisions.has(decision.id)}
                          onChange={() => handleSelectDecision(decision.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => handleDecisionSelect(decision)}>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {decision.reference}
                          </div>
                          <div className="text-sm text-gray-500">
                            {decision.business_name || 'Unknown Business'}
                          </div>
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {decision.summary}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => handleDecisionSelect(decision)}>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(decision.decision_status)}`}>
                          {getStatusIcon(decision.decision_status)}
                          {decision.decision_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 cursor-pointer" onClick={() => handleDecisionSelect(decision)}>
                        {decision.industry_sector}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 cursor-pointer" onClick={() => handleDecisionSelect(decision)}>
                        <Timestamp value={decision.decision_date} format="date-only" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {decision.pdf_url && (
                            <a
                              href={decision.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete All Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          disabled={totalCount === 0}
        >
          <TrashIcon className="h-5 w-5" />
          Delete All Decisions
        </button>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <OmbudsmanImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={selectedDecisions.size > 0 ? handleDeleteSelected : handleDeleteAll}
          title={selectedDecisions.size > 0 ? "Delete Selected Decisions" : "Delete All Decisions"}
          message={
            selectedDecisions.size > 0 
              ? `Are you sure you want to delete ${selectedDecisions.size} selected decisions? This action cannot be undone.`
              : "Are you sure you want to delete ALL ombudsman decisions? This action cannot be undone."
          }
          confirmText="Delete"
          isDestructive={true}
        />
      )}
    </div>
  );
};

export default OmbudsmanDecisionsView;
